import { useCallback } from 'react';

import { useApolloClient } from '@apollo/client';
import { useStoreContext } from 'features/store';

import {
  LoyaltyServiceOfferFragmentDoc,
  LoyaltyServiceOfferRedemptionType,
  useLoyaltyServiceOfferLazyQuery,
} from 'generated/graphql-gateway';
import { useLoyaltyUserId } from 'state/loyalty/hooks/use-loyalty-user';
import { LoyaltyOffer, appOfferToLoyaltyOffer } from 'state/loyalty/types';
import { useServiceModeLive } from 'state/service-mode';

import { ServiceOffer } from '../types';
import { mapServiceOffer } from '../utils';
import { getLoyaltyServiceServiceMode } from '../utils/service-mode';

/**
 * Resolve a single ON ("loyalty-service") offer by its `loyaltyEngineId`.
 * Reads the normalized Apollo cache first (offers written by the browse list),
 * and falls back to a by-id network query when the offer is not cached (e.g. a
 * deep link straight to the detail route). Pass `forceNetwork` to bypass the
 * cache and always refetch.
 */
export type GetServiceOfferById = (
  loyaltyEngineId: string,
  options?: { forceNetwork?: boolean }
) => Promise<LoyaltyOffer | undefined>;

/**
 * Returns a stable {@link GetServiceOfferById} callback. Kept independent of
 * `ServiceOffersContext` so it can be composed anywhere; the app-wide provider
 * calls it and exposes the result on context to make the getter globally
 * available.
 */
export function useGetServiceOfferById(): GetServiceOfferById {
  const client = useApolloClient();
  const serviceMode = useServiceModeLive();
  const { store } = useStoreContext();
  const storeId = store?.number;
  const { loyaltyUserId, loading: loyaltyUserIdLoading } = useLoyaltyUserId();

  const [runOfferQuery] = useLoyaltyServiceOfferLazyQuery({
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  return useCallback<GetServiceOfferById>(
    async (loyaltyEngineId, options) => {
      if (!loyaltyEngineId) {
        return undefined;
      }

      if (!options?.forceNetwork) {
        // Prefer offer from the browse list if it exists to avoid unnecessary network requests.
        const cached = client.readFragment<ServiceOffer>({
          id: client.cache.identify({ __typename: 'LoyaltyServiceOffer', loyaltyEngineId }),
          fragment: LoyaltyServiceOfferFragmentDoc,
          fragmentName: 'LoyaltyServiceOfferFragment',
        });
        if (cached) {
          return appOfferToLoyaltyOffer(mapServiceOffer(cached));
        }
      }

      // Cache miss (or forced refresh): the offer may not be in the browse list,
      // so fetch it by id. Wait until loyalty identity restoration settles; the
      // shared auth link adds a token when one is available.
      if (loyaltyUserIdLoading) {
        return undefined;
      }

      const variables = {
        id: loyaltyEngineId,
        serviceMode: getLoyaltyServiceServiceMode(serviceMode),
        storeId,
        redemptionTypes: [LoyaltyServiceOfferRedemptionType.STANDARD],
        includeOfferGroups: true,
        omitInvalids: false,
      };

      const { data } = await runOfferQuery({ variables });
      const serviceOffer = data?.loyaltyServiceOffer;
      return serviceOffer ? appOfferToLoyaltyOffer(mapServiceOffer(serviceOffer)) : undefined;
    },
    // The settled identity intentionally recreates this getter so mounted detail
    // consumers resolve again after login or logout, even though optional auth is
    // attached by the shared Apollo link rather than passed as a query variable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [client, loyaltyUserId, loyaltyUserIdLoading, runOfferQuery, serviceMode, storeId]
  );
}
